/**
 * ライセンス状態管理
 * - localStorage キャッシュ (1時間 TTL)
 * - CF Workers /license エンドポイントで確認
 * - Clerk JWT で認証（Authorization: Bearer）
 */

import { getAdminApiKey } from "../admin";

const WORKERS_URL =
	import.meta.env.VITE_WORKERS_URL ??
	"https://stripe-gateway.fucina.workers.dev";
const CACHE_KEY = "shironukiya_license";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1時間

export interface LicenseState {
	active: boolean;
	plan: "monthly" | "yearly" | null;
	expires_at: string | null;
	portal_url: string | null;
	cached_at: number;
	// 拡張フィールド（subtask_433a以降）
	customer_email?: string | null;
	subscription_start?: string | null;
	current_period_end?: string | null;
	price_amount?: number | null;
	price_currency?: string | null;
}

function isExpired(state: LicenseState): boolean {
	return Date.now() - state.cached_at > CACHE_TTL_MS;
}

export function getCachedLicense(): LicenseState | null {
	try {
		const raw = localStorage.getItem(CACHE_KEY);
		if (!raw) return null;
		const state: LicenseState = JSON.parse(raw);
		if (isExpired(state)) {
			localStorage.removeItem(CACHE_KEY);
			return null;
		}
		return state;
	} catch {
		return null;
	}
}

function setCachedLicense(
	state: Omit<LicenseState, "cached_at">,
): LicenseState {
	const full: LicenseState = { ...state, cached_at: Date.now() };
	try {
		localStorage.setItem(CACHE_KEY, JSON.stringify(full));
	} catch {
		// Private browsing — ignore
	}
	return full;
}

export function clearLicenseCache(): void {
	try {
		localStorage.removeItem(CACHE_KEY);
	} catch {
		// ignore
	}
}

/**
 * ライセンス状態を取得する
 * 1. キャッシュがあればそれを返す
 * 2. なければ /license を呼び出す（Clerk JWT付き）
 */
export async function fetchLicenseState(params?: {
	forceRefresh?: boolean;
	authToken?: string | null;
}): Promise<LicenseState> {
	if (!params?.forceRefresh) {
		const cached = getCachedLicense();
		if (cached) return cached;
	}

	const url = new URL(`${WORKERS_URL}/license`);
	url.searchParams.set("project", "shironukiya-saas");

	try {
		const headers: Record<string, string> = {};
		if (params?.authToken) {
			headers.Authorization = `Bearer ${params.authToken}`;
		}
		const adminKey = getAdminApiKey();
		if (adminKey) headers["X-Admin-Key"] = adminKey;

		const res = await fetch(url.toString(), { headers });
		if (!res.ok) {
			return setCachedLicense({
				active: false,
				plan: null,
				expires_at: null,
				portal_url: null,
			});
		}

		const data: Omit<LicenseState, "cached_at"> = await res.json();
		return setCachedLicense(data);
	} catch {
		// ネットワークエラー時はキャッシュを返す (フォールバック)
		return {
			active: false,
			plan: null,
			expires_at: null,
			portal_url: null,
			cached_at: Date.now(),
		};
	}
}

/**
 * Checkout セッションを作成してStripeにリダイレクト
 */
export async function redirectToCheckout(
	plan: "monthly" | "yearly",
	authToken?: string | null,
): Promise<void> {
	const siteUrl = "https://shironukiya.fucina.casa";
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	if (authToken) {
		headers.Authorization = `Bearer ${authToken}`;
	}

	const res = await fetch(`${WORKERS_URL}/checkout`, {
		method: "POST",
		headers,
		body: JSON.stringify({
			project: "shironukiya-saas",
			plan,
			successUrl: `${siteUrl}/checkout/success`,
			cancelUrl: siteUrl,
		}),
	});

	if (!res.ok) {
		throw new Error("Checkout セッションの作成に失敗しました");
	}

	const data = (await res.json()) as {
		url?: string;
		already_active?: boolean;
		redirect_url?: string;
	};

	// すでにアクティブな場合はredirect_urlへリダイレクト（二重課金防止）
	if (data.already_active && data.redirect_url) {
		window.location.href = data.redirect_url;
		return;
	}

	if (!data.url) {
		throw new Error("Checkout URLが取得できませんでした");
	}

	window.location.href = data.url;
}

/** アクティブなライセンスを持っているか（同期）*/
export function isPremium(state: LicenseState | null): boolean {
	return state?.active === true;
}
